// scheduling/pages/PoolAuditTab.jsx
// Admin-only pool audit (§6, Trello #257).
//
// Two things live here, both admin-on-demand rather than a live dashboard:
//
//  1. FIFO-bypass audit. Pools are first-in, first-out, and a self-claim is
//     server-enforced to take the head of the queue. Manager ASSIGNMENT is the one
//     path that can reach past someone — legitimately, it's the manager's call —
//     so every assignment records the assignee's position in the longest-waiting
//     queue at that moment. Rank > 1 means somebody waiting longer was skipped.
//     The purpose is spotting a pattern (§6: "consistently skipping higher-ranked
//     available staff to assign work to preferred individuals"), not policing one
//     decision.
//
//     Rank is NULL when the assignee wasn't checked in at all — they were never in
//     the queue, so there was no position to skip. Those are shown separately so
//     they don't read as bypasses.
//
//  2. Failed PE writes. A claim's local state and its PE write are not atomic: the
//     claim is never rolled back (the staffer has already been told the job is
//     theirs), so a PE failure leaves RPRS and PE disagreeing and needs a human.
//     This is the only place that surfaces.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { POOL_META } from '../utils/poolEligibility';
import { fetchPoolAudit } from '../services/poolApi';
import PoolConfigEditor from '../components/pool/PoolConfigEditor';

const PE_JOB_URL = (i) => `https://bmss.pehosted.com/PE/Job/Job/${i}?panel=0`;

function fmtWhen(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

const RANGES = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
];

export default function PoolAuditTab() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchPoolAudit(days)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Split assignments three ways so the interesting case isn't buried.
  const { skips, fairPicks, notQueued } = useMemo(() => {
    const rows = data?.assigned || [];
    return {
      skips: rows.filter(r => (r.FifoRankAtAssign || 0) > 1),
      fairPicks: rows.filter(r => r.FifoRankAtAssign === 1),
      notQueued: rows.filter(r => r.FifoRankAtAssign == null),
    };
  }, [data]);

  // Who is doing the skipping, and how often. This is the actual §6 signal.
  const byManager = useMemo(() => {
    const m = new Map();
    for (const r of skips) {
      const key = r.AssignedByName || `Staff ${r.AssignedByManagerId}`;
      const cur = m.get(key) || { name: key, count: 0, worstRank: 0 };
      cur.count += 1;
      cur.worstRank = Math.max(cur.worstRank, r.FifoRankAtAssign || 0);
      m.set(key, cur);
    }
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [skips]);

  if (loading) return <div className="sched-home-loading"><div className="sched-home-loading-text">Loading pool audit…</div></div>;
  if (error) return <div className="sched-placeholder"><p style={{ color: '#DC2626' }}>{error}</p></div>;

  const Row = ({ r, showRank = true }) => (
    <tr className="sched-mgr-row">
      <td>{fmtWhen(r.ClaimedAt)}</td>
      <td>{r.AssignedByName || '—'}</td>
      <td><strong>{r.ClaimedByName || '—'}</strong></td>
      {showRank && (
        <td className="sched-mgr-td-right">
          {r.FifoRankAtAssign == null ? (
            <span className="sched-pool-audit-noqueue" title="Not checked in as available — they were never in the queue">—</span>
          ) : (
            <span className={r.FifoRankAtAssign > 1 ? 'sched-pool-audit-rank-bad' : 'sched-pool-audit-rank-ok'}>
              {r.FifoRankAtAssign}
            </span>
          )}
        </td>
      )}
      <td>
        <span className="sched-pool-chip sched-pool-chip--sm" style={{ background: POOL_META[r.PoolCode]?.color || '#6B7280' }}>
          {POOL_META[r.PoolCode]?.label || r.PoolCode}
        </span>
      </td>
      <td>
        <div style={{ fontSize: 12 }}>{r.ClientName}</div>
        <div style={{ fontSize: 10.5, color: '#9CA3AF' }}>{r.JobName}</div>
      </td>
      <td className="sched-mgr-td-right">{Math.round((Number(r.BudgetHours) || 0) * 100) / 100}</td>
      <td>
        {r.PEJobIndex && (
          <a className="sched-pool-history-pe" href={PE_JOB_URL(r.PEJobIndex)} target="_blank" rel="noopener noreferrer">PE ↗</a>
        )}
      </td>
    </tr>
  );

  return (
    <div className="sched-mgr">
      {/* Config sits above the report, collapsed: an admin looking at whether the pools
          are behaving is the same person who'd retune them, so both belong on one
          screen. It loads its own data lazily on first expand. */}
      <PoolConfigEditor />

      <div className="sched-pool-audit-head">
        <div>
          <h3 className="sched-pool-audit-title">Pool assignment audit</h3>
          <p className="sched-pool-audit-blurb">
            Pools are first-in, first-out within each difficulty, and self-claims are
            forced to take the oldest job of their difficulty. Manager assignment is the one path that can reach past
            someone who has waited longer — that&rsquo;s allowed, and recorded here.
            Look for <strong>patterns</strong> rather than individual decisions.
          </p>
        </div>
        <div className="sched-pool-audit-ranges">
          {RANGES.map(r => (
            <button
              key={r.days}
              type="button"
              className={`sched-mgr-pill${days === r.days ? ' sched-mgr-pill--active' : ''}`}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Failed PE writes: the operational problem, so it goes first ── */}
      {data?.peFailed?.length > 0 && (
        <div className="sched-pool-audit-block sched-pool-audit-block--alert">
          <div className="sched-pool-audit-block-title">
            ⚠ {data.peFailed.length} pool {data.peFailed.length === 1 ? 'job' : 'jobs'} failed to write to Practice Engine
          </div>
          <div className="sched-pool-audit-block-sub">
            These were claimed in RPRS but PE didn&rsquo;t take the change, so the two
            systems disagree. The claim is deliberately not rolled back — the staffer
            was already told the job is theirs — so each needs fixing by hand in PE.
          </div>
          <table className="sched-mgr-table">
            <thead>
              <tr>
                <th>When</th><th>Claimed by</th><th>Failed step</th><th>Error</th><th>Job</th><th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.peFailed.map(r => (
                <tr key={r.PoolJobId} className="sched-mgr-row">
                  <td>{fmtWhen(r.ClaimedAt || r.PooledAt)}</td>
                  <td>{r.ClaimedByName || '—'}</td>
                  <td><code className="sched-pool-audit-step">{r.PeWriteStep || 'unknown'}</code></td>
                  <td className="sched-pool-audit-err">{r.PeWriteError || '—'}</td>
                  <td>
                    <div style={{ fontSize: 12 }}>{r.ClientName}</div>
                    <div style={{ fontSize: 10.5, color: '#9CA3AF' }}>{r.JobName}</div>
                  </td>
                  <td>
                    {r.PEJobIndex && (
                      <a className="sched-pool-history-pe" href={PE_JOB_URL(r.PEJobIndex)} target="_blank" rel="noopener noreferrer">PE ↗</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Summary ── */}
      <div className="sched-pool-audit-stats">
        <div className="sched-pool-audit-stat">
          <span className="sched-pool-audit-stat-val">{data?.assigned?.length || 0}</span>
          <span className="sched-pool-audit-stat-lbl">assignments</span>
        </div>
        <div className="sched-pool-audit-stat">
          <span className="sched-pool-audit-stat-val" style={{ color: skips.length ? '#DC2626' : '#16A34A' }}>{skips.length}</span>
          <span className="sched-pool-audit-stat-lbl">skipped the queue</span>
        </div>
        <div className="sched-pool-audit-stat">
          <span className="sched-pool-audit-stat-val">{fairPicks.length}</span>
          <span className="sched-pool-audit-stat-lbl">took rank 1</span>
        </div>
        <div className="sched-pool-audit-stat">
          <span className="sched-pool-audit-stat-val">{notQueued.length}</span>
          <span className="sched-pool-audit-stat-lbl">weren&rsquo;t in the queue</span>
        </div>
      </div>

      {byManager.length > 0 && (
        <div className="sched-pool-audit-block">
          <div className="sched-pool-audit-block-title">Queue skips by manager</div>
          <div className="sched-pool-audit-block-sub">
            One skip is routine. The same name repeatedly is the thing §6 exists to surface.
          </div>
          <div className="sched-pool-audit-mgrs">
            {byManager.map(m => (
              <div key={m.name} className="sched-pool-audit-mgr">
                <span className="sched-pool-audit-mgr-name">{m.name}</span>
                <span className="sched-pool-audit-mgr-count">
                  {m.count} skip{m.count === 1 ? '' : 's'} · deepest rank {m.worstRank}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="sched-pool-audit-block">
        <div className="sched-pool-audit-block-title">
          Assignments that reached past someone ({skips.length})
        </div>
        {skips.length === 0 ? (
          <div className="sched-pool-audit-none">
            No queue skips in this window — every assignment went to the
            longest-waiting eligible person, or to someone who wasn&rsquo;t checked in.
          </div>
        ) : (
          <table className="sched-mgr-table">
            <thead>
              <tr>
                <th>When</th><th>Assigned by</th><th>Assigned to</th>
                <th className="sched-mgr-th-right">Queue rank</th>
                <th>Pool</th><th>Job</th>
                <th className="sched-mgr-th-right">Budget</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>{skips.map(r => <Row key={r.PoolJobId} r={r} />)}</tbody>
          </table>
        )}
      </div>

      {(fairPicks.length > 0 || notQueued.length > 0) && (
        <div className="sched-pool-audit-block">
          <div className="sched-pool-audit-block-title">
            Everything else ({fairPicks.length + notQueued.length})
          </div>
          <table className="sched-mgr-table">
            <thead>
              <tr>
                <th>When</th><th>Assigned by</th><th>Assigned to</th>
                <th className="sched-mgr-th-right">Queue rank</th>
                <th>Pool</th><th>Job</th>
                <th className="sched-mgr-th-right">Budget</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {[...fairPicks, ...notQueued].map(r => <Row key={r.PoolJobId} r={r} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
